import { useMemo } from 'react';
import { useCarrierCatalogue, useRouteCatalogue } from '@my/directory-api/hooks';
import {
  ferryCarrierKey,
  fileCarrierEntry,
  fileLinkedFerryCarrierEntry,
  fileLinkedFerryRouteEntries,
  fileRouteEntry,
  type CrossingCarrier,
  type CrossingRoute,
} from './catalogue';

/**
 * The suite's list of carriers and crossings, as the pickers and the journey
 * planner read it: the catalogue file Directory bundles, never the ferry
 * service.
 *
 * <p>
 * A carrier the file links to Ferry (Stena, Seatruck) is offered under its
 * Ferry key, and each of its coded routes as two one-way legs under their
 * Ferry keys (`route.stena.duho`, `route.stena.hodu`), so a pick names the
 * leg Ferry books. Every other carrier and route is offered under its file
 * key, both ways. A pick saved under either key style resolves here. What
 * Ferry knows about a crossing (ids, port clocks, accounts, sailings) is
 * asked for only when a booking needs it, by `useFerryCrossing`.
 * </p>
 */
export interface CrossingCatalogue {
  /** Every entry under every key it answers to. */
  routes: CrossingRoute[];
  carriers: CrossingCarrier[];
  routeByKey: Map<string, CrossingRoute>;
  carrierByKey: Map<string, CrossingCarrier>;
  /** What the Settings cards offer: each carrier and crossing once, under the key a new pick saves as. */
  pickableCarriers: CrossingCarrier[];
  pickableRoutes: CrossingRoute[];
  isPending: boolean;
  fileError: Error | null;
}

export function useCrossingCatalogue(): CrossingCatalogue {
  const fileRoutes = useRouteCatalogue();
  const fileCarriers = useCarrierCatalogue();

  return useMemo(() => {
    const fileCarrierEntries = (fileCarriers.data?.carriers ?? []).map(fileCarrierEntry);
    const fileRouteEntries = (fileRoutes.data?.routes ?? []).map(fileRouteEntry);
    const fileCarrierByKey = new Map(fileCarrierEntries.map((c) => [c.key, c]));
    const ferryCodeOf = (route: CrossingRoute) =>
      fileCarrierByKey.get(route.carrierKey)?.ferryCarrierCode ?? null;

    // The Ferry-keyed side of each linked entry, as Directory resolves the key.
    const ferryKeyedCarriers = fileCarrierEntries.flatMap((c) => {
      const entry = fileLinkedFerryCarrierEntry(c);
      return entry ? [entry] : [];
    });
    const ferryKeyedRoutes = fileRouteEntries.flatMap((entry) => {
      const carrierCode = ferryCodeOf(entry);
      return carrierCode ? fileLinkedFerryRouteEntries(entry, carrierCode) : [];
    });

    // Offered under the Ferry key when linked, else under the file key. A
    // linked carrier's route the file has no codes for yet is offered under
    // its file key but grouped with the carrier's other legs.
    const pickableCarriers = fileCarrierEntries.map((c) => fileLinkedFerryCarrierEntry(c) ?? c);
    const pickableRoutes = fileRouteEntries.flatMap((entry) => {
      const carrierCode = ferryCodeOf(entry);
      if (!carrierCode) return [entry];
      const legs = fileLinkedFerryRouteEntries(entry, carrierCode);
      return legs.length > 0 ? legs : [{ ...entry, carrierKey: ferryCarrierKey(carrierCode) }];
    });

    const routes = [...fileRouteEntries, ...ferryKeyedRoutes];
    const carriers = [...fileCarrierEntries, ...ferryKeyedCarriers];

    return {
      routes,
      carriers,
      routeByKey: new Map(routes.map((r) => [r.key, r])),
      carrierByKey: new Map(carriers.map((c) => [c.key, c])),
      pickableCarriers,
      pickableRoutes,
      isPending: fileRoutes.isPending || fileCarriers.isPending,
      fileError: fileRoutes.error ?? fileCarriers.error ?? null,
    };
  }, [
    fileRoutes.data,
    fileRoutes.isPending,
    fileRoutes.error,
    fileCarriers.data,
    fileCarriers.isPending,
    fileCarriers.error,
  ]);
}
